import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { captureDueArgs, captureEventStart } from '@goaltree/backend/convex/lib/captureDates'
import type { HabitMeasure } from '@goaltree/backend/convex/lib/habitMeasure'
import {
  type LiveItem,
  liveKeysFor,
  liveToCaptureItem,
  updateLiveItems,
} from '@goaltree/backend/convex/lib/livePreview'
import { addDaysStr } from '@goaltree/backend/convex/lib/mustDo'
import { FREE_HABIT_LIMIT } from '@goaltree/backend/convex/lib/usage'
import { useAction, useMutation, useQuery } from 'convex/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useReadCaptureImage } from '@/hooks/use-photo-tasks'
import { localDateStr } from '@/hooks/use-today-tasks'
import { optimisticCreateEvent } from '@/lib/events-optimistic'
import { haptic } from '@/lib/haptics'
import { optimisticQuickTask } from '@/lib/nodes-optimistic'
import { isConnectionError, withReconnect } from '@/lib/reconnect'
import { playCreatedSound } from '@/lib/sounds'
import { transcriptFromResults } from '@/lib/speech-transcript'
import {
  type DictationFailure,
  type StreamDictation,
  isStreamDictationSupported,
  startStreamDictation,
} from '@/lib/stream-dictation'
import { getUsageLimit, handleUsageLimit, isHabitLimit, openHabitUpsell } from '@/lib/usage-limit'
import type { CaptureConversation } from '@/store/capture.store'

export type VoiceStatus = 'idle' | 'listening' | 'paused' | 'processing' | 'review' | 'error'

export type CaptureKind = 'goal' | 'task' | 'event' | 'habit'

// How long the speaker pauses before we re-read the transcript for live items.
const PREVIEW_PAUSE_MS = 900
// At most one "item detected" cue this often, however many items arrive at once.
const DETECT_CUE_GAP_MS = 400

export type CaptureItem = {
  key: number
  kind: CaptureKind
  title: string
  category: string
  when: 'today' | 'tomorrow' | 'thisWeek' | null
  period: 'daily' | 'weekday' | 'weekend' | 'weekly' | 'days' | null
  days?: number[]
  timeOfDay: 'morning' | 'afternoon' | 'evening' | null
  // Tasks: due day (YYYY-MM-DD), time (HH:MM), length and place.
  dueDate: string | null
  time: string | null
  durationMinutes: number | null
  location: string | null
  // A picked place suggestion's coordinates.
  locationCoords?: { lat: number; lon: number }
  // Events: no time said.
  allDay?: boolean
  // Tasks: index (key) of an event in the same dump, for "Due before <day>?".
  relatedEventIndex?: number
  // Tasks: the due date is the related event's day (the user accepted the suggestion).
  dueBefore?: boolean
  similar: { id: Id<'nodes'>; title: string; onToday?: boolean } | null
  // Habits: a number / fasting habit's measure; oneOff for a single fast.
  measure?: HabitMeasure
  oneOff?: boolean
  // The live card this item continues, so review picks up where it left off.
  liveKey?: string
  selected: boolean
  // needsPro: a habit past the Free cap, left uncreated.
  status: 'pending' | 'creating' | 'created' | 'error' | 'needsPro'
  goalId: Id<'nodes'> | null
}

export type CaptureEdit = Partial<
  Pick<
    CaptureItem,
    | 'kind'
    | 'dueDate'
    | 'time'
    | 'durationMinutes'
    | 'location'
    | 'locationCoords'
    | 'allDay'
    | 'dueBefore'
  >
>

export type CaptureResult = {
  goals: number
  tasks: number
  events: number
  habits: number
}

type WebSpeechRecognition = {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null
  onerror: ((event: WebSpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
}

type WebSpeechRecognitionEvent = {
  results: { length: number; [i: number]: { isFinal: boolean; 0: { transcript: string } } }
}

type WebSpeechRecognitionErrorEvent = {
  error: string
}

type WebSpeechRecognitionCtor = new () => WebSpeechRecognition

export function isSpeechRecognitionSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
  )
}

export function isVoiceCaptureSupported(): boolean {
  return isStreamDictationSupported() || isSpeechRecognitionSupported()
}

// Set once the server dictation path fails (e.g. Free plan) so later dumps on
// this page go straight to Web Speech.
let streamUnavailable = false

export const AUTO_STOP_MS = 120_000
// Once something's been said: no new words for this long shows "Wrapping up in 3…",
// then stops.
const SILENCE_WARN_MS = 8_000
const SILENCE_STOP_MS = 12_000

const SPEECH_LANGS: Record<string, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  es: 'es-ES',
  fr: 'fr-FR',
  pt: 'pt-BR',
  zh: 'zh-CN',
}

const HABIT_TIMES = { morning: '08:00', afternoon: '13:00', evening: '19:00' } as const

function getSpeechRecognition(): WebSpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  return (
    (window as Window & { SpeechRecognition?: WebSpeechRecognitionCtor }).SpeechRecognition ??
    (window as Window & { webkitSpeechRecognition?: WebSpeechRecognitionCtor })
      .webkitSpeechRecognition ??
    null
  )
}

function joinText(a: string, b: string) {
  return [a.trim(), b.trim()].filter(Boolean).join(' ')
}

// All-day dated tasks are stored at local noon (matches the inbox composer).
function localNoon(daysAhead: number) {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate() + daysAhead, 12).getTime()
}

export function useVoiceCapture() {
  const { t, i18n } = useTranslation()
  const [status, setStatusState] = useState<VoiceStatus>('idle')
  const [transcript, setTranscript] = useState('')
  const [items, setItems] = useState<CaptureItem[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [spokenMs, setSpokenMs] = useState<number | null>(null)
  const [fromImage, setFromImage] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [retryable, setRetryable] = useState<'offline' | 'other' | null>(null)
  const [, setTick] = useState(0)
  const retryRef = useRef<(() => void) | null>(null)

  const statusRef = useRef<VoiceStatus>('idle')
  const recognitionRef = useRef<WebSpeechRecognition | null>(null)
  const dictationRef = useRef<StreamDictation | null>(null)
  // Final words still arriving from a stopped stream; read before the transcript is.
  const flushRef = useRef<Promise<void>>(Promise.resolve())
  // Bumped whenever the transcript is rebuilt, so a late flush can't overwrite it.
  const epochRef = useRef(0)
  const dropsRef = useRef(0)
  const transcriptRef = useRef('')
  // Text committed before the current recognition session (starter prompt + pre-pause speech).
  const baseRef = useRef('')
  const accumulatedMsRef = useRef(0)
  const sessionStartRef = useRef<number | null>(null)
  const quickEndsRef = useRef(0)
  // Last new words, tap or resume; silence only counts after the first words.
  const activityRef = useRef(0)
  const heardRef = useRef(false)

  const extractAction = useAction(api.voice.extractCaptureItems)
  const extractConversationAction = useAction(api.voice.extractFromConversation)
  const readImage = useReadCaptureImage()
  const dictationSession = useAction(api.dictation.dictationSession)
  const previewAction = useAction(api.voice.previewCaptureItems)
  const [liveItems, setLiveItems] = useState<LiveItem[]>([])
  const liveRef = useRef(liveItems)
  liveRef.current = liveItems
  const goneRef = useRef<LiveItem[]>([])
  const cueRef = useRef(0)
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { celebrationSound?: boolean }
    | null
    | undefined
  const soundRef = useRef(false)
  soundRef.current = !!prefs?.celebrationSound
  const [previewTick, setPreviewTick] = useState(0)
  const previewRef = useRef({ sent: '', inflight: false, session: 0 })
  const createGoalAction = useAction(api.voice.createGoalFromTitle)
  const createQuickTask = useMutation(api.nodes.createQuickTask).withOptimisticUpdate(
    optimisticQuickTask,
  )
  const createEvent = useMutation(api.events.create).withOptimisticUpdate(optimisticCreateEvent)
  const recordCapture = useMutation(api.nodes.recordCapture)

  function setStatus(s: VoiceStatus) {
    statusRef.current = s
    setStatusState(s)
    if (s === 'listening' || s === 'idle') setFromImage(false)
  }

  function setText(text: string) {
    transcriptRef.current = text
    setTranscript(text)
  }

  function elapsed() {
    return (
      accumulatedMsRef.current +
      (sessionStartRef.current !== null ? Date.now() - sessionStartRef.current : 0)
    )
  }

  function endSession() {
    const rec = recognitionRef.current
    recognitionRef.current = null
    rec?.stop()
    const dictation = dictationRef.current
    dictationRef.current = null
    if (dictation) {
      const epoch = epochRef.current
      const base = baseRef.current
      flushRef.current = dictation.finish().then((text) => {
        if (epochRef.current === epoch) setText(joinText(base, text))
      })
    }
    accumulatedMsRef.current = elapsed()
    sessionStartRef.current = null
  }

  function restartTranscript(text: string) {
    epochRef.current++
    baseRef.current = text
    setText(text)
  }

  function fail(msg: string) {
    endSession()
    setErrorMessage(msg)
    setStatus('error')
  }

  async function extract(text: string) {
    if (!text.trim()) {
      toast(t('voice.noItems'))
      setStatus('idle')
      return
    }
    const live = liveRef.current
    retryRef.current = () => {
      setStatus('processing')
      void extract(text)
    }
    await review(async () => {
      // Never drop items the user already saw live: fall back to them, details unset.
      try {
        const r = await withReconnect(
          () => extractAction({ transcript: text, language: i18n.language, today: localDateStr() }),
          () => setReconnecting(true),
          live.length ? 2 : 3,
        )
        if (r.items.length || !live.length) return r
      } catch (err) {
        if (!live.length || getUsageLimit(err)) throw err
      }
      if (statusRef.current === 'processing') toast(t('voice.checkLiveItems'))
      return { items: live.map(liveToCaptureItem) }
    }, 'voice.noItems')
  }

  // Resolves true once there are items to review.
  async function review(
    run: () => Promise<{ items: Omit<CaptureItem, 'key' | 'selected' | 'status' | 'goalId'>[] }>,
    emptyKey: string,
  ) {
    setRetryable(null)
    try {
      const result = await run()
      setReconnecting(false)
      if (statusRef.current !== 'processing') return false
      if (result.items.length === 0) {
        toast(t(emptyKey))
        setStatus('idle')
        return false
      }
      const liveKeys = liveKeysFor(liveRef.current, result.items)
      setItems(
        result.items.map((it, key) => ({
          ...it,
          key,
          liveKey: liveKeys[key],
          selected: !it.similar,
          status: 'pending' as const,
          goalId: null,
        })),
      )
      setStatus('review')
      return true
    } catch (err) {
      setReconnecting(false)
      if (statusRef.current !== 'processing') return false
      if (handleUsageLimit(err)) {
        setStatus('idle')
        return false
      }
      console.error('[capture] extract failed', err)
      const offline = isConnectionError(err)
      const msg = offline
        ? t('voice.errOffline')
        : err instanceof Error && err.cause
          ? err.message
          : t('voice.errExtract')
      if (!offline) toast.error(msg)
      setErrorMessage(msg)
      setRetryable(retryRef.current ? (offline ? 'offline' : 'other') : null)
      setStatus('error')
      return false
    }
  }

  function extractConversation(source: CaptureConversation) {
    setFromImage(false)
    endSession()
    restartTranscript('')
    setItems([])
    setSpokenMs(null)
    setStatus('processing')
    retryRef.current = () => {
      setStatus('processing')
      void extractConversation(source)
    }
    return review(
      () =>
        extractConversationAction({
          ...source,
          language: i18n.language,
          today: localDateStr(),
        }),
      'voice.conversationEmpty',
    )
  }

  // A screenshot or photo of notes, read like a brain dump.
  function submitImage(file: File) {
    endSession()
    restartTranscript('')
    setItems([])
    clearLive()
    setSpokenMs(null)
    setFromImage(true)
    setStatus('processing')
    retryRef.current = () => void submitImage(file)
    return review(async () => {
      try {
        return await withReconnect(
          () => readImage(file),
          () => setReconnecting(true),
        )
      } catch (err) {
        if (getUsageLimit(err) || isConnectionError(err)) throw err
        throw new Error(t('voice.imageError'), { cause: err })
      }
    }, 'voice.imageNoItems')
  }

  function startSession() {
    if (!streamUnavailable && isStreamDictationSupported()) return startStream()
    startSpeech()
  }

  function startStream() {
    const dictation = startStreamDictation({
      connect: () =>
        dictationSession({
          language: i18n.language,
          tzOffsetMin: new Date().getTimezoneOffset(),
        }),
      onText: (text) => {
        if (dictationRef.current === dictation) setText(joinText(baseRef.current, text))
      },
      onFail: (f) => {
        if (dictationRef.current === dictation) onStreamFail(f)
      },
    })
    dictationRef.current = dictation
    sessionStartRef.current = Date.now()
    setErrorMessage(null)
    setStatus('listening')
  }

  function onStreamFail(f: DictationFailure) {
    dictationRef.current = null
    if (f.kind === 'mic') return fail(t('voice.errMicDenied'))
    if (f.kind === 'unavailable') {
      if (getUsageLimit(f.error)) {
        handleUsageLimit(f.error)
        return reset()
      }
      console.warn('[dictation] falling back to Web Speech', f.error)
      streamUnavailable = true
      if (!isSpeechRecognitionSupported()) return fail(t('voice.errNetwork'))
      endSession()
      baseRef.current = transcriptRef.current
      return startSpeech()
    }
    // Dropped mid-dump: reconnect once with what we have, then just finish.
    endSession()
    if (++dropsRef.current > 1) return stop()
    baseRef.current = transcriptRef.current
    startStream()
  }

  function startSpeech() {
    const Impl = getSpeechRecognition()
    if (!Impl) {
      fail(t('voice.browserNotSupportedDesc'))
      return
    }
    const recognition = new Impl()
    recognition.lang = SPEECH_LANGS[i18n.language] ?? 'en-US'
    recognition.continuous = true
    recognition.interimResults = true
    recognitionRef.current = recognition

    // Identity checks drop late events from a session we already stopped (pause/stop),
    // which would otherwise re-append its text on top of the committed base.
    recognition.onresult = (event: WebSpeechRecognitionEvent) => {
      if (recognitionRef.current !== recognition) return
      setText(joinText(baseRef.current, transcriptFromResults(event.results)))
    }

    recognition.onerror = (event: WebSpeechRecognitionErrorEvent) => {
      if (recognitionRef.current !== recognition) return
      // Silence isn't an error; onend restarts the engine.
      if (event.error === 'no-speech' || event.error === 'aborted') return
      fail(
        event.error === 'network'
          ? t('voice.errNetwork')
          : event.error === 'not-allowed' || event.error === 'audio-capture'
            ? t('voice.errMicDenied')
            : t('voice.errRecognition', { code: event.error }),
      )
    }

    // Browsers end recognition on their own after silence or a time limit; only Done
    // or the cap should end a dump, so carry the transcript into a fresh session.
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return
      if (statusRef.current !== 'listening') return
      const startedAt = sessionStartRef.current
      endSession()
      quickEndsRef.current =
        startedAt !== null && Date.now() - startedAt < 1000 ? quickEndsRef.current + 1 : 0
      // An engine that keeps dying instantly would spin forever; fall back to finishing.
      if (quickEndsRef.current >= 3) return stop()
      baseRef.current = transcriptRef.current
      startSpeech()
    }

    try {
      recognition.start()
      sessionStartRef.current = Date.now()
      setErrorMessage(null)
      setStatus('listening')
    } catch {
      fail(t('voice.errStart'))
    }
  }

  function start(prefix?: string) {
    endSession()
    accumulatedMsRef.current = 0
    quickEndsRef.current = 0
    dropsRef.current = 0
    heardRef.current = false
    restartTranscript(prefix ?? '')
    setItems([])
    clearLive()
    setSpokenMs(null)
    startSession()
  }

  // A prompt tapped mid-dump continues the transcript instead of restarting it.
  async function addPrompt(phrase: string) {
    if (statusRef.current !== 'listening' && statusRef.current !== 'paused') return start(phrase)
    endSession()
    const epoch = epochRef.current
    await flushRef.current
    if (epochRef.current !== epoch) return
    restartTranscript(joinText(transcriptRef.current, phrase))
    startSession()
  }

  function pause() {
    if (statusRef.current !== 'listening') return
    endSession()
    setStatus('paused')
  }

  async function resume() {
    if (statusRef.current !== 'paused') return
    const epoch = epochRef.current
    await flushRef.current
    if (statusRef.current !== 'paused' || epochRef.current !== epoch) return
    baseRef.current = transcriptRef.current
    startSession()
  }

  async function stop() {
    endSession()
    setSpokenMs(accumulatedMsRef.current)
    setStatus('processing')
    const epoch = epochRef.current
    await flushRef.current
    if (statusRef.current !== 'processing' || epochRef.current !== epoch) return
    void extract(transcriptRef.current)
  }

  function submitText(text: string) {
    setFromImage(false)
    endSession()
    restartTranscript(text.trim())
    setItems([])
    setSpokenMs(null)
    setStatus('processing')
    void extract(text)
  }

  function toggle(key: number, selected?: boolean) {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, selected: selected ?? !it.selected } : it)),
    )
  }

  function rename(key: number, title: string) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, title } : it)))
  }

  function setWhen(key: number, when: CaptureItem['when']) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, when } : it)))
  }

  function editTask(key: number, p: CaptureEdit) {
    patch(key, p)
  }

  function patch(key: number, p: Partial<CaptureItem>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...p } : it)))
  }

  async function createSelected(): Promise<{ counts: CaptureResult; ids: Id<'nodes'>[] }> {
    const counts: CaptureResult = { goals: 0, tasks: 0, events: 0, habits: 0 }
    const ids: Id<'nodes'>[] = []
    const queue = items.filter((it) => it.selected && it.status === 'pending' && it.title.trim())
    // Tasks and habits are instant; goals take seconds each, so run them last.
    const ordered = [
      ...queue.filter((it) => it.kind !== 'goal'),
      ...queue.filter((it) => it.kind === 'goal'),
    ]
    let heldBack = 0
    for (const it of ordered) {
      const title = it.title.trim()
      if (it.kind === 'habit' && heldBack > 0) {
        heldBack++
        patch(it.key, { status: 'needsPro' })
        continue
      }
      patch(it.key, { status: 'creating' })
      try {
        if (it.kind === 'goal') {
          const { goalId, title: storedTitle } = await createGoalAction({ title })
          patch(it.key, { status: 'created', goalId, title: storedTitle })
          ids.push(goalId)
          counts.goals++
        } else if (it.kind === 'event' && it.dueDate) {
          const id = await createEvent({
            title,
            startsAt: captureEventStart(it.dueDate, it.allDay ? null : it.time),
            ...(it.allDay || !it.time
              ? { allDay: true }
              : it.durationMinutes && { durationMinutes: it.durationMinutes }),
            ...(it.location && { location: it.location }),
            ...(it.location && it.locationCoords && { locationCoords: it.locationCoords }),
          })
          patch(it.key, { status: 'created' })
          ids.push(id)
          counts.events++
        } else if (it.kind === 'task' || it.kind === 'event') {
          const id = await createQuickTask({
            title,
            // Said "today"/"tomorrow" → straight into that day's Must do lane.
            ...(it.when === 'today' && { mustDoOn: localDateStr() }),
            ...(it.when === 'tomorrow' && { mustDoOn: addDaysStr(localDateStr(), 1) }),
            ...(it.when === 'thisWeek' &&
              !it.dueDate && { dueDate: localNoon((7 - new Date().getDay()) % 7) }),
            ...captureDueArgs(it),
            ...(it.location && { location: it.location }),
            ...(it.location && it.locationCoords && { locationCoords: it.locationCoords }),
            ...(it.dueBefore && it.dueDate && { dueBefore: true }),
          })
          patch(it.key, { status: 'created' })
          ids.push(id)
          counts.tasks++
        } else {
          const id = await createQuickTask({
            title,
            category: it.category.toLowerCase(),
            isHabit: true,
            recursPeriod: it.period ?? 'daily',
            ...(it.days && { recursDays: it.days }),
            ...(it.measure && { measure: it.measure }),
            ...(it.oneOff && { oneOff: true }),
            recurrenceTzOffset: new Date().getTimezoneOffset(),
            ...(it.timeOfDay && { recurrenceTime: HABIT_TIMES[it.timeOfDay] }),
          })
          patch(it.key, { status: 'created' })
          ids.push(id)
          counts.habits++
        }
      } catch (err) {
        if (isHabitLimit(err)) {
          heldBack++
          patch(it.key, { status: 'needsPro' })
          continue
        }
        if (!handleUsageLimit(err))
          toast.error(err instanceof Error ? err.message : t('voice.errCreate'))
        patch(it.key, { status: 'error' })
      }
    }
    if (heldBack > 0)
      toast(t('habitLimit.heldBack', { count: heldBack }), {
        description: t('habitLimit.heldBackDesc', { limit: FREE_HABIT_LIMIT }),
        action: { label: t('habitLimit.upgrade'), onClick: openHabitUpsell },
      })
    if (counts.goals + counts.tasks + counts.events + counts.habits > 0) {
      playCreatedSound()
      await recordCapture({
        goals: counts.goals,
        tasks: counts.tasks + counts.events,
        habits: counts.habits,
      }).catch(() => {})
    }
    return { counts, ids }
  }

  function reset() {
    endSession()
    accumulatedMsRef.current = 0
    restartTranscript('')
    setItems([])
    clearLive()
    setSpokenMs(null)
    setErrorMessage(null)
    retryRef.current = null
    setRetryable(null)
    setStatus('idle')
  }

  function retry() {
    const run = retryRef.current
    if (!run || statusRef.current !== 'error') return
    setErrorMessage(null)
    setRetryable(null)
    run()
  }

  // Failed for lack of signal: pick up again the moment it's back.
  useEffect(() => {
    if (status !== 'error' || retryable !== 'offline') return
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [status, retryable]) // eslint-disable-line react-hooks/exhaustive-deps

  function clearLive() {
    previewRef.current = { sent: '', inflight: false, session: previewRef.current.session + 1 }
    goneRef.current = []
    setLiveItems([])
  }

  function cueDetected() {
    const now = Date.now()
    if (now - cueRef.current < DETECT_CUE_GAP_MS) return
    cueRef.current = now
    // Only with the mic off: a sound while listening could end up in the transcript.
    if (statusRef.current === 'paused' && soundRef.current) playCreatedSound()
    else haptic('light')
  }

  // While talking, re-read the transcript at each pause in speech so items pop in
  // as they're mentioned. One request at a time; a newer transcript waits its turn.
  useEffect(() => {
    if (status !== 'listening' && status !== 'paused') return
    const text = transcript.trim()
    const p = previewRef.current
    if (text.length < 12 || text === p.sent || p.inflight) return
    const id = setTimeout(() => {
      const session = p.session
      p.inflight = true
      p.sent = text
      previewAction({
        transcript: text,
        language: i18n.language,
        today: localDateStr(),
        detailed: true,
      })
        .then((r) => {
          if (previewRef.current.session !== session) return
          const next = updateLiveItems(liveRef.current, goneRef.current, r.items)
          goneRef.current = next.gone
          liveRef.current = next.items
          setLiveItems(next.items)
          if (next.fresh.length) cueDetected()
        })
        .catch(() => {})
        .finally(() => {
          if (previewRef.current.session !== session) return
          previewRef.current.inflight = false
          setPreviewTick((n) => n + 1)
        })
    }, PREVIEW_PAUSE_MS)
    return () => clearTimeout(id)
  }, [transcript, status, previewTick])

  const stopRef = useRef(stop)
  stopRef.current = stop

  useEffect(() => {
    if (status !== 'listening') return
    if (transcript.trim() !== baseRef.current.trim()) heardRef.current = true
    activityRef.current = Date.now()
  }, [transcript, status])

  function nudge() {
    activityRef.current = Date.now()
  }

  function silence() {
    return statusRef.current === 'listening' && heardRef.current
      ? Date.now() - activityRef.current
      : 0
  }

  useEffect(() => {
    if (status !== 'listening') return
    const id = setInterval(() => {
      if (elapsed() >= AUTO_STOP_MS || silence() >= SILENCE_STOP_MS) stopRef.current()
      else setTick((n) => n + 1)
    }, 250)
    return () => clearInterval(id)
  }, [status])

  useEffect(() => {
    return () => {
      const rec = recognitionRef.current
      recognitionRef.current = null
      rec?.stop()
      dictationRef.current?.close()
      dictationRef.current = null
    }
  }, [])

  return {
    status,
    transcript,
    items,
    liveItems,
    errorMessage,
    elapsedMs: elapsed(),
    spokenMs,
    fromImage,
    reconnecting,
    retry: retryable ? retry : null,
    // Seconds until a silent dump stops itself (3, 2, 1), else null.
    wrapUpIn:
      silence() >= SILENCE_WARN_MS
        ? Math.min(3, Math.ceil((SILENCE_STOP_MS - silence()) / 1000))
        : null,
    nudge,
    start,
    addPrompt,
    pause,
    resume,
    stop,
    submitText,
    submitImage,
    extractConversation,
    toggle,
    rename,
    setWhen,
    editTask,
    createSelected,
    reset,
  }
}
